import * as ReactDOM from "react-dom";
import { useRef } from "react";
import { useYplusPremiumAccess } from "../../../../../global/paywall/useYplusPremiumAccess";
import { dispatchAiSummaryState } from "./actionBarShared";

export interface SummaryDataItem {
    title?: string;
    body?: string | string[];
}

export interface QuoteSummaryItem {
    quote: string;
    meta: string;
}

interface ArticleHeader1280AiSummaryProps {
    summaryData?: SummaryDataItem[];
    articleId: string;
    lang?: string;
    variant?: "desktop" | "mobile";
    desktopSummaryMountId?: string;
}

const MOBILE_SUMMARY_DISMISS_RATIO = 0.6;
const MOBILE_SUMMARY_DISMISS_VELOCITY = 0.65;
const MOBILE_SUMMARY_DISMISS_MIN_OFFSET = 18;
const MOBILE_SUMMARY_CLOSE_ANIMATION_MS = 260;

export const toSummaryBodyText = (body: unknown): string => {
    if (typeof body === "string") {
        return body;
    }

    if (Array.isArray(body)) {
        return body
            .map((item) => (typeof item === "string" ? item : (item == null ? "" : String(item))))
            .filter((item) => item)
            .join("\n\n");
    }

    if (body == null) {
        return "";
    }

    return String(body);
};

export const cleanLineText = (line: string) => {
    return toSummaryBodyText(line)
        .replace(/^\s*(\*+|•|\.)\s*/, "")
        .replace(/\*+/g, "")
        .trim();
};

export const getQuoteItems = (body: unknown): QuoteSummaryItem[] => {
    const bodyText = toSummaryBodyText(body);
    if (!bodyText) {
        return [];
    }

    const groups = bodyText
        .split(/\n\s*\n/)
        .map((group) => group.split("\n").map((line) => cleanLineText(line)).filter((line) => line))
        .filter((lines) => lines.length > 0);

    return groups
        .map((lines) => ({
            quote: lines[0] || "",
            meta: lines.slice(1).join(" "),
        }))
        .filter((item) => item.quote);
};

export const getSummarySections = (summaryData: SummaryDataItem[]) => {
    let summaryItem: SummaryDataItem = null;
    let quotesItem: SummaryDataItem = null;

    summaryData.forEach((item, index) => {
        if (!item) {
            return;
        }

        const title = (item.title || "").toLowerCase();
        if (!summaryItem && (title.indexOf("תקציר") !== -1 || title.indexOf("summary") !== -1)) {
            summaryItem = item;
            return;
        }
        if (!quotesItem && (title.indexOf("ציטוט") !== -1 || title.indexOf("quote") !== -1)) {
            quotesItem = item;
            return;
        }
        if (!summaryItem && index === 0) {
            summaryItem = item;
            return;
        }
        if (!quotesItem && index === 1) {
            quotesItem = item;
        }
    });

    return {
        summaryItem,
        quotesItem,
    };
};

const getAiSummaryLabels = (lang: string) => {
    let triggerText = "תקציר AI";
    let triggerLabel = "פתיחת תקציר AI";
    let summaryTitle = "תקציר AI";
    let summaryCaption = "תוכן זה נוצר על ידי בינה מלאכותית";
    let closeLabel = "סגירת תקציר AI";

    if (lang === "en") {
        triggerText = "AI Summary";
        triggerLabel = "Open AI summary";
        summaryTitle = "AI Summary";
        summaryCaption = "This content was generated by artificial intelligence";
        closeLabel = "Close AI summary";
    }

    if (lang === "ru") {
        triggerText = "AI кратко";
        triggerLabel = "Открыть AI кратко";
        summaryTitle = "AI кратко";
        summaryCaption = "Этот контент создан искусственным интеллектом";
        closeLabel = "Закрыть AI кратко";
    }

    if (lang === "ar") {
        triggerText = "ملخص AI";
        triggerLabel = "فتح ملخص AI";
        summaryTitle = "ملخص AI";
        summaryCaption = "تم إنشاء هذا المحتوى بواسطة الذكاء الاصطناعي";
        closeLabel = "إغلاق ملخص AI";
    }

    return {
        triggerText,
        triggerLabel,
        summaryTitle,
        summaryCaption,
        closeLabel,
    };
};

export const ArticleHeader1280AiSummary: any = ({
    summaryData = [],
    articleId,
    lang = "",
    variant = "desktop",
    desktopSummaryMountId,
}: ArticleHeader1280AiSummaryProps) => {
    const hasPremiumAccess = useYplusPremiumAccess();
    const isMobileVariant = variant === "mobile";
    const [isSummaryVisible, setIsSummaryVisible] = React.useState(false);
    const [isQuotesExpanded, setIsQuotesExpanded] = React.useState(false);
    const [isSummaryClosing, setIsSummaryClosing] = React.useState(false);
    const [isDraggingSheet, setIsDraggingSheet] = React.useState(false);
    const [dragOffsetY, setDragOffsetY] = React.useState(0);
    const summarySheetRef: any = useRef(null);
    const dragStartYRef: any = useRef(0);
    const dragDistanceRef: any = useRef(0);
    const dragPointerIdRef: any = useRef(null);
    const dragSheetHeightRef: any = useRef(0);
    const dragLastYRef: any = useRef(0);
    const dragLastMoveTimeRef: any = useRef(0);
    const dragVelocityYRef: any = useRef(0);
    const mobileScrollLockState: any = useRef({
        scrollY: 0,
        bodyPosition: "",
        bodyTop: "",
        bodyLeft: "",
        bodyRight: "",
        bodyWidth: "",
        bodyOverflow: "",
    });
    const [desktopSummaryMountNode, setDesktopSummaryMountNode] = React.useState(null as HTMLElement | null);
    const { summaryItem, quotesItem } = getSummarySections(summaryData);
    const quotesSectionId = `articleSummaryQuotes_${articleId || "default"}`;
    const summarySectionId = `articleSummary_${articleId || "default"}`;
    const summaryBodyText = toSummaryBodyText(summaryItem && summaryItem.body);
    const quotesBodyText = toSummaryBodyText(quotesItem && quotesItem.body);
    const quoteItems = getQuoteItems(quotesBodyText);
    const labels = getAiSummaryLabels(lang);
    const shouldRenderSummary = summaryData.some((item) => {
        if (!item) {
            return false;
        }

        const title = typeof item.title === "string" ? item.title.trim() : "";
        const body = toSummaryBodyText(item.body).trim();
        return !!(title || body);
    });
    const usesExternalDesktopMount = !isMobileVariant && !!desktopSummaryMountId;

    React.useEffect(() => {
        if (!usesExternalDesktopMount || typeof document === "undefined") {
            setDesktopSummaryMountNode(null);
            return;
        }

        const resolveMountNode = () => document.getElementById(desktopSummaryMountId);

        const existingMountNode = resolveMountNode();
        if (existingMountNode) {
            setDesktopSummaryMountNode(existingMountNode);
            return;
        }

        const observer = new MutationObserver(() => {
            const mountNode = resolveMountNode();
            if (mountNode) {
                setDesktopSummaryMountNode(mountNode);
                observer.disconnect();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [usesExternalDesktopMount, desktopSummaryMountId, isSummaryVisible]);

    React.useEffect(() => {
        dispatchAiSummaryState(isSummaryVisible);
    }, [isSummaryVisible]);

    React.useEffect(() => {
        if (!isSummaryVisible) {
            setIsQuotesExpanded(false);
            setIsDraggingSheet(false);
            setDragOffsetY(0);
            dragDistanceRef.current = 0;
            dragPointerIdRef.current = null;
            dragSheetHeightRef.current = 0;
            dragLastYRef.current = 0;
            dragLastMoveTimeRef.current = 0;
            dragVelocityYRef.current = 0;
        }
    }, [isSummaryVisible]);

    React.useEffect(() => {
        if (!isMobileVariant || !isSummaryVisible) {
            return;
        }

        if (typeof document === "undefined" || typeof window === "undefined") {
            return;
        }

        const body = document.body;
        const scrollLock = mobileScrollLockState.current;
        scrollLock.scrollY = window.pageYOffset || window.scrollY || 0;
        scrollLock.bodyPosition = body.style.position;
        scrollLock.bodyTop = body.style.top;
        scrollLock.bodyLeft = body.style.left;
        scrollLock.bodyRight = body.style.right;
        scrollLock.bodyWidth = body.style.width;
        scrollLock.bodyOverflow = body.style.overflow;

        body.style.position = "fixed";
        body.style.top = `-${scrollLock.scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden";

        return () => {
            body.style.position = scrollLock.bodyPosition;
            body.style.top = scrollLock.bodyTop;
            body.style.left = scrollLock.bodyLeft;
            body.style.right = scrollLock.bodyRight;
            body.style.width = scrollLock.bodyWidth;
            body.style.overflow = scrollLock.bodyOverflow;
            window.scrollTo(0, scrollLock.scrollY);
        };
    }, [isMobileVariant, isSummaryVisible]);

    if (!shouldRenderSummary || !hasPremiumAccess) {
        return null;
    }

    const getSheetHeight = () => {
        const sheetElement = summarySheetRef.current;
        if (!sheetElement || !sheetElement.getBoundingClientRect) {
            return 0;
        }

        return sheetElement.getBoundingClientRect().height || 0;
    };

    const getDismissOffset = () => {
        const currentSheetHeight = Math.max(getSheetHeight(), dragSheetHeightRef.current, 0);
        const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 0 : 0;
        return Math.max(currentSheetHeight + 48, viewportHeight ? viewportHeight * 0.72 : 0);
    };

    const finishDragTracking = () => {
        dragPointerIdRef.current = null;
        dragStartYRef.current = 0;
        dragDistanceRef.current = 0;
        dragSheetHeightRef.current = 0;
        dragLastYRef.current = 0;
        dragLastMoveTimeRef.current = 0;
        dragVelocityYRef.current = 0;
    };

    const closeSummaryWithAnimation = (targetOffset?: number) => {
        if (!isSummaryVisible || isSummaryClosing) {
            return;
        }

        const dismissOffset = Math.max(typeof targetOffset === "number" ? targetOffset : 0, getDismissOffset());
        setIsDraggingSheet(false);
        setIsSummaryClosing(true);
        setDragOffsetY(dismissOffset);
        finishDragTracking();

        window.setTimeout(() => {
            setIsSummaryVisible(false);
            setIsSummaryClosing(false);
            setDragOffsetY(0);
        }, MOBILE_SUMMARY_CLOSE_ANIMATION_MS);
    };

    const handleToggleQuotesSection = () => {
        setIsQuotesExpanded(!isQuotesExpanded);
    };

    const handleToggleSummary = () => {
        if (isSummaryVisible) {
            if (isMobileVariant) {
                closeSummaryWithAnimation();
                return;
            }

            setIsSummaryVisible(false);
            return;
        }

        setIsSummaryVisible(true);
        setIsSummaryClosing(false);
    };

    const handleCloseSummary = () => {
        if (!isSummaryVisible) {
            return;
        }

        if (!isMobileVariant) {
            setIsSummaryVisible(false);
            return;
        }

        closeSummaryWithAnimation();
    };

    const handleSheetDragStart = (e) => {
        if (!isMobileVariant || !isSummaryVisible || isSummaryClosing) {
            return;
        }

        if (typeof e.button === "number" && e.button !== 0) {
            return;
        }

        const pointerId = typeof e.pointerId === "number" ? e.pointerId : null;
        const now = Date.now();

        dragStartYRef.current = typeof e.clientY === "number" ? e.clientY : 0;
        dragDistanceRef.current = 0;
        dragPointerIdRef.current = pointerId;
        dragSheetHeightRef.current = getSheetHeight();
        dragLastYRef.current = dragStartYRef.current;
        dragLastMoveTimeRef.current = now;
        dragVelocityYRef.current = 0;
        setIsDraggingSheet(true);
        setIsSummaryClosing(false);

        if (e && e.currentTarget && e.currentTarget.setPointerCapture && pointerId !== null) {
            try {
                e.currentTarget.setPointerCapture(pointerId);
            } catch (error) {
            }
        }

        if (e && e.preventDefault) {
            e.preventDefault();
        }
    };

    const handleSheetDragMove = (e) => {
        if (!isMobileVariant || !isDraggingSheet) {
            return;
        }

        if (dragPointerIdRef.current !== null && typeof e.pointerId === "number" && e.pointerId !== dragPointerIdRef.current) {
            return;
        }

        const currentY = typeof e.clientY === "number" ? e.clientY : dragStartYRef.current;
        const delta = currentY - dragStartYRef.current;
        const nextOffset = delta > 0 ? delta : 0;
        const now = Date.now();
        const timeDelta = now - dragLastMoveTimeRef.current;

        dragDistanceRef.current = nextOffset;
        if (timeDelta > 0) {
            dragVelocityYRef.current = (currentY - dragLastYRef.current) / timeDelta;
        }
        dragLastYRef.current = currentY;
        dragLastMoveTimeRef.current = now;
        setDragOffsetY(nextOffset);

        if (e && e.preventDefault) {
            e.preventDefault();
        }
    };

    const handleSheetDragEnd = (e) => {
        if (!isMobileVariant || !isDraggingSheet) {
            return;
        }

        if (dragPointerIdRef.current !== null && typeof e.pointerId === "number" && e.pointerId !== dragPointerIdRef.current) {
            return;
        }

        if (e && e.currentTarget && e.currentTarget.releasePointerCapture && dragPointerIdRef.current !== null) {
            try {
                e.currentTarget.releasePointerCapture(dragPointerIdRef.current);
            } catch (error) {
            }
        }

        const shouldCloseByDrag = dragDistanceRef.current >= dragSheetHeightRef.current * MOBILE_SUMMARY_DISMISS_RATIO
            || (dragDistanceRef.current >= MOBILE_SUMMARY_DISMISS_MIN_OFFSET && dragVelocityYRef.current > MOBILE_SUMMARY_DISMISS_VELOCITY);
        setIsDraggingSheet(false);

        if (shouldCloseByDrag) {
            closeSummaryWithAnimation(dragDistanceRef.current);
            return;
        }

        setDragOffsetY(0);
        finishDragTracking();
    };

    const summarySheetStyle = isMobileVariant && (isDraggingSheet || isSummaryClosing || dragOffsetY > 0)
        ? isDraggingSheet
            ? {
                transform: `translateY(${dragOffsetY}px)`,
                transition: "none",
                animation: "none",
            }
            : isSummaryClosing
                ? {
                    transform: `translateY(${dragOffsetY}px)`,
                    transition: "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 200ms ease",
                    animation: "none",
                }
                : {
                    transform: `translateY(${dragOffsetY}px)`,
                    animation: "none",
                }
        : null;

    const backdropFadeHeight = Math.max(dragSheetHeightRef.current, getSheetHeight(), 1);
    const dragBackdropOpacity = Math.max(0, 1 - Math.min(dragOffsetY / backdropFadeHeight, 1));
    const summaryBackdropStyle = isMobileVariant && (isDraggingSheet || isSummaryClosing || dragOffsetY > 0)
        ? isDraggingSheet
            ? {
                opacity: dragBackdropOpacity,
                transition: "none",
            }
            : {
                opacity: dragBackdropOpacity,
            }
        : null;

    const summaryPanel = (
        <div
            id={summarySectionId}
            className={`articleHeaderAiSummary1280 ${isSummaryVisible ? "visible" : "hidden"}`}
            aria-hidden={!isSummaryVisible}
        >
            <button
                type="button"
                className="articleHeaderAiSummaryCloseIcon"
                onClick={handleCloseSummary}
                aria-label={labels.closeLabel}
                tabIndex={isSummaryVisible ? 0 : -1}
            />
            <div className="articleHeaderAiSummaryTop">
                <div className="articleHeaderAiSummaryHead">
                    <div className="articleHeaderAiSummaryTitle">
                        <span className="articleHeaderAiSummaryTitleText">{labels.summaryTitle}</span>
                        <span className="articleHeaderAiSummaryTitleIcon" aria-hidden="true" />
                    </div>
                    <p className="articleHeaderAiSummaryCaption">{labels.summaryCaption}</p>
                </div>
            </div>

            {!!summaryBodyText && (
                <div className="articleHeaderAiSummarySection">
                    <p className="articleHeaderAiSummaryMainText">{cleanLineText(summaryBodyText).replace(/\s+/g, " ")}</p>
                </div>
            )}

            {!!quotesBodyText && (
                <div className="articleHeaderAiSummarySection quotesSection" dir="rtl">
                    <button
                        type="button"
                        className={`quotesToggleBtn ${isQuotesExpanded ? "expanded" : "collapsed"}`}
                        onClick={handleToggleQuotesSection}
                        aria-expanded={isQuotesExpanded}
                        aria-controls={quotesSectionId}
                    >
                        <span className={`quotesToggleIcon ${isQuotesExpanded ? "expanded" : "collapsed"}`} aria-hidden="true" />
                        <span className="quotesToggleText" dir="rtl">ציטוטים מרכזיים</span>
                    </button>
                    <div
                        id={quotesSectionId}
                        className={`quotesSectionContent ${isQuotesExpanded ? "expanded" : "collapsed"}`}
                        aria-hidden={!isQuotesExpanded}
                        dir="rtl"
                    >
                        {quoteItems.map((item, index) => (
                            <div className="quotesItem" key={`quote_item_${index}`}>
                                <div className="quotesItemText">
                                    <div className="quotesItemQuote" dir="rtl">{item.quote}</div>
                                    {!!item.meta && <div className="quotesItemMeta" dir="rtl">{item.meta}</div>}
                                </div>
                                <div className="quotesItemBulletContainer" aria-hidden="true">
                                    <span className="quotesItemBullet" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const desktopSummaryPanel = usesExternalDesktopMount
        ? (desktopSummaryMountNode
            ? ReactDOM.createPortal(summaryPanel, desktopSummaryMountNode)
            : null)
        : summaryPanel;

    return (
        <div className={`articleHeaderAiSummaryHost ${isMobileVariant ? "mobile" : "desktop"}`}>
            <button
                type="button"
                className={isMobileVariant ? "articleHeaderActionBarItem articleHeaderActionBarItemAi" : "action-btn ai-btn"}
                onClick={handleToggleSummary}
                aria-expanded={isSummaryVisible}
                aria-controls={summarySectionId}
                aria-label={labels.triggerLabel}
            >
                {isMobileVariant ? (
                    <>
                        <span className="articleHeaderActionBarText">{labels.triggerText}</span>
                        <span className="articleHeaderActionBarIcon articleHeaderActionBarIconAi" />
                    </>
                ) : (
                    <>
                        <span className="action-btn-icon ai-icon" />
                        <span className="action-btn-text">{labels.triggerText}</span>
                    </>
                )}
            </button>

            {isMobileVariant ? (
                isSummaryVisible && (
                    <div className={`articleHeaderActionBarSummaryModal ${isSummaryClosing ? "closing" : ""}`}>
                        <button
                            type="button"
                            className="articleHeaderActionBarSummaryBackdrop"
                            onClick={handleCloseSummary}
                            aria-label={labels.closeLabel}
                            style={summaryBackdropStyle}
                        />
                        <div
                            ref={summarySheetRef}
                            className="articleHeaderActionBarSummarySheet"
                            role="dialog"
                            aria-modal="true"
                            aria-label={labels.summaryTitle}
                            style={summarySheetStyle}
                        >
                            <div
                                className="articleHeaderActionBarSummaryGestureZone"
                                onPointerDown={handleSheetDragStart}
                                onPointerMove={handleSheetDragMove}
                                onPointerUp={handleSheetDragEnd}
                                onPointerCancel={handleSheetDragEnd}
                                aria-hidden="true"
                            >
                                <div className="articleHeaderActionBarSummaryDragHandle" />
                            </div>
                            <div className="articleHeaderActionBarSummaryContent">
                                {summaryPanel}
                            </div>
                        </div>
                    </div>
                )
            ) : (
                desktopSummaryPanel
            )}
        </div>
    );
};

ArticleHeader1280AiSummary.siteScriptName = "ArticleHeader1280AiSummary";